import api from './api';

// Import des constantes de statut
import { RESERVATION_STATUS } from '../constants/statusCodes';

/**
 * Service API pour la gestion des QR codes
 * PHASE 1.1: Confirmation Pickup/Return
 */
class QRCodeService {
  
  /**
   * Générer un QR code pour pickup ou return
   * @param {number} reservationId - ID de la réservation
   * @param {string} type - 'pickup' ou 'return'
   * @returns {Promise} Résultat avec QR code en base64
   */
  static async generateQRCode(reservationId, type) {
    try {
      const response = await api.get(`/qr-codes/generate/${reservationId}/${type}`);
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error generating QR code:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur lors de la génération du QR code'
      };
    }
  }

  /**
   * Valider un QR code scanné
   * @param {string} qrData - Données du QR code scanné
   * @param {number} reservationId - ID de la réservation
   * @returns {Promise} Résultat de la validation
   */
  static async validateQRCode(qrData, reservationId) {
    try {
      const response = await api.post('/qr-codes/validate', {
        qrData,
        reservationId
      });
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error validating QR code:', error);
      const errorPayload = error.response?.data || {};
      return {
        success: false,
        error: errorPayload.error || 'Erreur lors de la validation du QR code',
        status: errorPayload.status,
        code: errorPayload.code,
        translationKey: errorPayload.translationKey || errorPayload.details?.translationKey,
        details: errorPayload.details
      };
    }
  }

  /**
   * Confirmer pickup via QR code ou code manuel avec support photos
   * @param {number} reservationId - ID de la réservation
   * @param {Object} confirmationData - Données de confirmation
   * @returns {Promise} Résultat de la confirmation
   */
  static async confirmPickup(reservationId, confirmationData) {
    try {
      // 🎯 NOUVELLE LOGIQUE: Utiliser la route avec photos pour assurer les condition reports
      const formData = new FormData();
      
      // Données obligatoires selon la nouvelle API
      formData.append('reservation_id', reservationId);
      formData.append('user_role', confirmationData.user_role || 'owner');
      
      // QR Code - adapter selon la structure des données reçues
      if (confirmationData.manualCode) {
        formData.append('qr_code', confirmationData.manualCode);
      } else if (confirmationData.qrData?.code) {
        formData.append('qr_code', confirmationData.qrData.code);
      } else if (confirmationData.qr_code) {
        formData.append('qr_code', confirmationData.qr_code);
      }
      
      // Notes optionnelles - consolider toutes les notes possibles
      const notes = confirmationData.notes || 
                   confirmationData.confirmation_notes || 
                   `Pickup confirmé via ${confirmationData.method || 'QR code'}`;
      formData.append('notes', notes);
      
      // Photos optionnelles
      console.log('🔍 [Frontend QRCodeService] Données photos reçues:', confirmationData.photos);
      console.log('🔍 [Frontend QRCodeService] Nombre de photos:', confirmationData.photos?.length || 0);
      
      if (confirmationData.photos && confirmationData.photos.length > 0) {
        console.log('🔍 [Frontend QRCodeService] Ajout des photos au FormData...');
        confirmationData.photos.forEach((photo, index) => {
          console.log(`🔍 [Frontend QRCodeService] Photo ${index}:`, photo);
          console.log(`🔍 [Frontend QRCodeService] Photo ${index} type:`, typeof photo);
          
          // 🔧 FIX: Les photos sont des objets avec une propriété 'file'
          let fileToUpload;
          if (photo instanceof File) {
            // Si c'est directement un File
            fileToUpload = photo;
            console.log(`🔍 [Frontend QRCodeService] Photo ${index} est un File direct`);
          } else if (photo.file && photo.file instanceof File) {
            // Si c'est un objet avec une propriété 'file'
            fileToUpload = photo.file;
            console.log(`🔍 [Frontend QRCodeService] Photo ${index} contient un File dans .file`);
          } else {
            console.error(`🔴 [Frontend QRCodeService] Photo ${index} format non supporté:`, photo);
            return; // Skip cette photo
          }
          
          console.log(`🔍 [Frontend QRCodeService] Photo ${index} File name:`, fileToUpload.name);
          console.log(`🔍 [Frontend QRCodeService] Photo ${index} File size:`, fileToUpload.size);
          console.log(`🔍 [Frontend QRCodeService] Photo ${index} File type:`, fileToUpload.type);
          
          formData.append('photos', fileToUpload);
        });
        console.log('🔍 [Frontend QRCodeService] FormData après ajout photos:', Array.from(formData.entries()));
      } else {
        console.log('🔍 [Frontend QRCodeService] Aucune photo à ajouter au FormData');
      }

      const response = await api.post('/qr-codes/confirm-pickup-with-photos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error confirming pickup:', error);
      console.log('🔍 [Frontend QRCodeService] Error status:', error.response?.status);
      console.log('🔍 [Frontend QRCodeService] Error data:', error.response?.data);
      const errorPayload = error.response?.data || {};
      console.log('🔍 [Frontend QRCodeService] Returning error object:', {
        success: false,
        error: errorPayload.error || 'Erreur lors de la confirmation du pickup',
        status: errorPayload.status,
        code: errorPayload.code,
        translationKey: errorPayload.translationKey || errorPayload.details?.translationKey,
        details: errorPayload.details
      });
      return {
        success: false,
        error: errorPayload.error || 'Erreur lors de la confirmation du pickup',
        status: errorPayload.status,
        code: errorPayload.code,
        translationKey: errorPayload.translationKey || errorPayload.details?.translationKey,
        details: errorPayload.details
      };
    }
  }

  /**
   * Confirmer return via QR code ou code manuel
   * @param {number} reservationId - ID de la réservation
   * @param {Object} confirmationData - Données de confirmation
   * @returns {Promise} Résultat de la confirmation
   */
  static async confirmReturn(reservationId, confirmationData) {
    try {
      // 🎯 NOUVELLE LOGIQUE: Utiliser la route avec photos pour assurer les condition reports
      const formData = new FormData();
      
      // Données obligatoires selon la nouvelle API
      formData.append('reservation_id', reservationId);
      formData.append('user_role', confirmationData.user_role || 'renter');
      
      // QR Code - adapter selon la structure des données reçues
      if (confirmationData.manualCode) {
        formData.append('qr_code', confirmationData.manualCode);
      } else if (confirmationData.qrData?.code) {
        formData.append('qr_code', confirmationData.qrData.code);
      } else if (confirmationData.qr_code) {
        formData.append('qr_code', confirmationData.qr_code);
      }
      
      // Notes optionnelles - consolider toutes les notes possibles
      const notes = confirmationData.notes || 
                   confirmationData.confirmation_notes || 
                   `Return confirmé via ${confirmationData.method || 'QR code'}`;
      formData.append('notes', notes);
      
      // Photos optionnelles
      console.log('🔍 [Frontend QRCodeService] Données photos reçues:', confirmationData.photos);
      console.log('🔍 [Frontend QRCodeService] Nombre de photos:', confirmationData.photos?.length || 0);
      
      if (confirmationData.photos && confirmationData.photos.length > 0) {
        console.log('🔍 [Frontend QRCodeService] Ajout des photos au FormData...');
        confirmationData.photos.forEach((photo, index) => {
          console.log(`🔍 [Frontend QRCodeService] Photo ${index}:`, photo);
          console.log(`🔍 [Frontend QRCodeService] Photo ${index} type:`, typeof photo);
          
          // 🔧 FIX: Les photos sont des objets avec une propriété 'file'
          let fileToUpload;
          if (photo instanceof File) {
            // Si c'est directement un File
            fileToUpload = photo;
            console.log(`🔍 [Frontend QRCodeService] Photo ${index} est un File direct`);
          } else if (photo.file && photo.file instanceof File) {
            // Si c'est un objet avec une propriété 'file'
            fileToUpload = photo.file;
            console.log(`🔍 [Frontend QRCodeService] Photo ${index} contient un File dans .file`);
          } else {
            console.error(`🔴 [Frontend QRCodeService] Photo ${index} format non supporté:`, photo);
            return; // Skip cette photo
          }
          
          console.log(`🔍 [Frontend QRCodeService] Photo ${index} File name:`, fileToUpload.name);
          console.log(`🔍 [Frontend QRCodeService] Photo ${index} File size:`, fileToUpload.size);
          console.log(`🔍 [Frontend QRCodeService] Photo ${index} File type:`, fileToUpload.type);
          
          formData.append('photos', fileToUpload);
        });
        console.log('🔍 [Frontend QRCodeService] FormData après ajout photos:', Array.from(formData.entries()));
      } else {
        console.log('🔍 [Frontend QRCodeService] Aucune photo à ajouter au FormData');
      }

      const response = await api.post('/qr-codes/confirm-return-with-photos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error confirming return:', error);
      const errorPayload = error.response?.data || {};
      return {
        success: false,
        error: errorPayload.error || 'Erreur lors de la confirmation du return',
        status: errorPayload.status,
        code: errorPayload.code,
        translationKey: errorPayload.translationKey || errorPayload.details?.translationKey,
        details: errorPayload.details
      };
    }
  }

  /**
   * Vérifier si l'utilisateur peut générer un QR code
   * @param {Object} reservation - Objet réservation
   * @param {string} type - 'pickup' ou 'return'
   * @param {number} userId - ID de l'utilisateur
   * @returns {boolean} Peut générer ou non
   */
  static canGenerateQRCode(reservation, type, userId) {
    if (!reservation || !userId) return false;
    
    // 🚫 BLOQUAGE: Pas d'accès QR si la réservation est dans un état final
    const blockedStatuses = [
      RESERVATION_STATUS.CANCELLED, 
      RESERVATION_STATUS.REFUNDED,
      RESERVATION_STATUS.EXPIRED,
      RESERVATION_STATUS.COMPLETED // Une fois complétée, plus besoin de QR
    ];
    
    if (blockedStatuses.includes(reservation.status)) return false;
    
    if (type === 'pickup') {
      // Le renter génère le QR pickup
      const canGenerate = userId === reservation.renter_id && !reservation.pickup_confirmed;
      return canGenerate;
    } else if (type === 'return') {
      // 🔧 FIX: L'owner génère le QR return (après pickup confirmé)
      // Il faut vérifier que l'utilisateur EST bien l'owner de la réservation
      const listingOwnerId = reservation.listing?.ownerId || reservation.Listing?.ownerId || reservation.listing?.owner?.id || reservation.Listing?.owner?.id;
      const canGenerate = userId === listingOwnerId && !reservation.return_confirmed && reservation.pickup_confirmed;
      console.log('🔍 [canGenerateQRCode return] userId:', userId, 'listingOwnerId:', listingOwnerId, 'pickup_confirmed:', reservation.pickup_confirmed, 'return_confirmed:', reservation.return_confirmed, 'result:', canGenerate);
      return canGenerate;
    }
    
    return false;
  }

  /**
   * Vérifier si l'utilisateur peut scanner un QR code
   * @param {Object} reservation - Objet réservation
   * @param {string} type - 'pickup' ou 'return'
   * @param {number} userId - ID de l'utilisateur
   * @returns {boolean} Peut scanner ou non
   */
  static canScanQRCode(reservation, type, userId) {
    // Sortie précoce silencieuse pour les cas évidents
    if (!reservation || !userId) {
      return false;
    }
    
    // Sortie précoce pour les réservations dans un état final (évite les logs répétitifs)
    const blockedStatuses = [
      RESERVATION_STATUS.CANCELLED, 
      RESERVATION_STATUS.REFUNDED,
      RESERVATION_STATUS.EXPIRED,
      RESERVATION_STATUS.COMPLETED
    ];
    
    if (blockedStatuses.includes(reservation.status)) {
      return false; // Sortie silencieuse pour éviter le spam de logs
    }
    
    // Logs seulement pour les réservations actives (évite le spam)
    console.log('� [canScanQRCode] ENTREE - type:', type, 'userId:', userId, 'reservation.id:', reservation?.id, 'status:', reservation.status);
    
    if (type === 'pickup') {
      // 🔧 FIX: L'owner scanne le QR pickup du renter
      const listingOwnerId = reservation.listing?.ownerId || reservation.Listing?.ownerId || reservation.listing?.owner?.id || reservation.Listing?.owner?.id;
      const canScan = userId === listingOwnerId && !reservation.pickup_confirmed;
      console.log('🔍 [canScanQRCode pickup] userId:', userId, 'listingOwnerId:', listingOwnerId, 'pickup_confirmed:', reservation.pickup_confirmed, 'result:', canScan);
      return canScan;
    } else if (type === 'return') {
      // 🔧 FIX: Le renter scanne le QR return de l'owner
      const canScan = userId === reservation.renter_id && !reservation.return_confirmed && reservation.pickup_confirmed;
      console.log('🔍 [canScanQRCode return] userId:', userId, 'renter_id:', reservation.renter_id, 'pickup_confirmed:', reservation.pickup_confirmed, 'return_confirmed:', reservation.return_confirmed, 'result:', canScan);
      return canScan;
    }
    
    return false;
  }

  /**
   * Obtenir le statut de confirmation pour l'affichage
   * @param {Object} reservation - Objet réservation
   * @returns {Object} Statut des confirmations
   */
  static getConfirmationStatus(reservation) {
    // 🚫 Si la réservation est dans un état final, pas de confirmations possibles
    const blockedStatuses = [
      RESERVATION_STATUS.CANCELLED, 
      RESERVATION_STATUS.REFUNDED,
      RESERVATION_STATUS.EXPIRED,
      RESERVATION_STATUS.COMPLETED
    ];
    
    if (blockedStatuses.includes(reservation.status)) {
      const status = reservation.status === RESERVATION_STATUS.CANCELLED ? 'cancelled' :
                     reservation.status === RESERVATION_STATUS.EXPIRED ? 'expired' :
                     reservation.status === RESERVATION_STATUS.REFUNDED ? 'refunded' :
                     'completed';
                     
      return {
        pickup: {
          confirmed: false,
          canConfirm: false,
          status: status
        },
        return: {
          confirmed: false,
          canConfirm: false,
          status: status
        }
      };
    }
    
    return {
      pickup: {
        confirmed: reservation.pickup_confirmed,
        canConfirm: !reservation.pickup_confirmed,
        status: reservation.pickup_confirmed ? 'confirmed' : 'pending'
      },
      return: {
        confirmed: reservation.return_confirmed,
        canConfirm: reservation.pickup_confirmed && !reservation.return_confirmed,
        status: reservation.return_confirmed ? 'confirmed' : 
                reservation.pickup_confirmed ? 'pending' : 'disabled'
      }
    };
  }

  /**
   * Formater les données QR pour l'affichage
   * @param {Object} qrData - Données du QR code
   * @returns {Object} Données formatées
   */
  static formatQRData(qrData) {
    try {
      const parsed = typeof qrData === 'string' ? JSON.parse(qrData) : qrData;
      return {
        reservationId: parsed.reservationId,
        type: parsed.type,
        code: parsed.code,
        timestamp: parsed.timestamp,
        expiresAt: parsed.expiresAt,
        isValid: new Date(parsed.expiresAt) > new Date()
      };
    } catch (error) {
      console.error('Error formatting QR data:', error);
      return null;
    }
  }

  /**
   * Refuser pickup avec photos et raison
   * @param {number} reservationId - ID de la réservation
   * @param {Object} refusalData - Données de refus
   * @returns {Promise} Résultat du refus
   */
  static async refusePickup(reservationId, refusalData) {
    try {
      // 🎯 NOUVELLE LOGIQUE: Utiliser la route de refus avec photos
      const formData = new FormData();
      
      // Données obligatoires selon l'API
      formData.append('reservation_id', reservationId);
      formData.append('user_role', refusalData.user_role || 'owner');
      formData.append('refusal_reason', refusalData.refusal_reason || 'Condition non conforme');
      
      // Notes optionnelles
      const notes = refusalData.notes || 
                   `Pickup refusé: ${refusalData.refusal_reason}`;
      formData.append('notes', notes);
      
      // Photos optionnelles
      console.log('🔍 [Frontend QRCodeService] Données photos refus reçues:', refusalData.photos);
      console.log('🔍 [Frontend QRCodeService] Nombre de photos refus:', refusalData.photos?.length || 0);
      
      if (refusalData.photos && refusalData.photos.length > 0) {
        console.log('🔍 [Frontend QRCodeService] Ajout des photos refus au FormData...');
        refusalData.photos.forEach((photo, index) => {
          console.log(`🔍 [Frontend QRCodeService] Photo refus ${index}:`, photo);
          
          // Gestion du format des photos (même logique que confirm)
          let fileToUpload;
          if (photo instanceof File) {
            fileToUpload = photo;
            console.log(`🔍 [Frontend QRCodeService] Photo refus ${index} est un File direct`);
          } else if (photo.file && photo.file instanceof File) {
            fileToUpload = photo.file;
            console.log(`🔍 [Frontend QRCodeService] Photo refus ${index} contient un File dans .file`);
          } else {
            console.error(`🔴 [Frontend QRCodeService] Photo refus ${index} format non supporté:`, photo);
            return; // Skip cette photo
          }
          
          console.log(`🔍 [Frontend QRCodeService] Photo refus ${index} File name:`, fileToUpload.name);
          console.log(`🔍 [Frontend QRCodeService] Photo refus ${index} File size:`, fileToUpload.size);
          console.log(`🔍 [Frontend QRCodeService] Photo refus ${index} File type:`, fileToUpload.type);
          
          formData.append('photos', fileToUpload);
        });
        console.log('🔍 [Frontend QRCodeService] FormData refus après ajout photos:', Array.from(formData.entries()));
      } else {
        console.log('🔍 [Frontend QRCodeService] Aucune photo refus à ajouter au FormData');
      }

      const response = await api.post('/qr-codes/refuse-pickup', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return {
        success: true,
        data: response.data.data
      };
    } catch (error) {
      console.error('Error refusing pickup:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Erreur lors du refus du pickup',
        status: error.response?.data?.status
      };
    }
  }

  /**
   * Vérifier si le navigateur supporte la caméra
   * @returns {Promise<boolean>} Support de la caméra
   */
  static async checkCameraSupport() {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return false;
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      
      // Arrêter immédiatement le stream de test
      stream.getTracks().forEach(track => track.stop());
      
      return true;
    } catch (error) {
      console.warn('Camera access check failed:', error);
      return false;
    }
  }

  /**
   * Constants pour les types de QR et statuts
   */
  static QR_CODE_TYPES = {
    PICKUP: 'pickup',
    RETURN: 'return'
  };

  static QR_VALIDATION_STATUS = {
    VALID: 'valid',
    EXPIRED: 'expired',
    INVALID: 'invalid',
    ALREADY_USED: 'already_used',
    WRONG_USER: 'wrong_user'
  };

  static CONFIRMATION_METHODS = {
    QR_CODE: 'qr_code',
    MANUAL_CODE: 'manual_code'
  };
}

export default QRCodeService;
